import React from "react";

import EditableText from "../../views/Docuclipper/EditableText";
import { Row, Form } from "react-bootstrap";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import TrackedButton from "../../views/Docuclipper/TrackedButton";

type Props = {
  rows: string[][];
  hasHeader?: boolean;
  csvDelimiter?: string;
  tableClassName?: string;
  tableRowClassName?: string;
  tableColumnClassName?: string;
  renderHeaderRowFn?: () => any;
  editableCells?: boolean;
  onSaveFieldNameEdit?: (newText: string, row: number, col: number) => any;
  enableMerge: boolean;
  onMerge?: (selectedRows: { [k: number]: boolean }) => any;
  onDelete?: (selectedRows: { [k: number]: boolean }) => any;
  maxHeight?: string;
};

const CsvToHtmlTable: React.SFC<Props> = ({
  rows,
  hasHeader,
  csvDelimiter,
  tableClassName,
  tableRowClassName,
  tableColumnClassName,
  renderHeaderRowFn,
  editableCells,
  onSaveFieldNameEdit,
  enableMerge,
  onMerge,
  onDelete,
  maxHeight,
}) => {
  const renderTableHeader = (row) => {
    if (row) {
      return (
        <thead>
          <tr>
            {row.map((column, i) => {
              return <th key={`${i}${column}`}>{column}</th>;
            })}
          </tr>
        </thead>
      );
    }
    return null;
  };
  const [selected, setSelected] = React.useState<{ [key: number]: boolean }>(
    []
  );

  const renderTableBody = (rows) => {
    return (
      <>
        <tbody>
          {rows.map((row, i) => (
            <tr className={tableRowClassName} key={i}>
              {enableMerge ? (
                <td>
                  <div className="d-flex align-items-center mt-3">
                    <Form.Check
                      style={{
                        position: "relative",
                        marginTop: 0,
                        marginLeft: 0,
                        transform: "scale(1.2)",
                      }}
                      onChange={(e) =>
                        setSelected({ ...selected, [i]: e.target.checked })
                      }
                      name={`selected-${i}`}
                      type="checkbox"
                      checked={selected[i]}
                    />
                  </div>
                </td>
              ) : null}
              {row.map((column, j) => (
                <td className={tableColumnClassName} key={`${i}${j}`}>
                  {editableCells ? (
                    <OverlayTrigger
                      popperConfig={{
                        modifiers: {
                          preventOverflow: {
                            enabled: false,
                          },
                        } as any,
                      }}
                      placement="top"
                      overlay={
                        <Tooltip id={`fix-data-${i}-${j}`}>Fix data</Tooltip>
                      }
                    >
                      <div id={`cell-${i}${j}`}>
                        <EditableText
                          initialValue={column}
                          onConfirm={
                            onSaveFieldNameEdit
                              ? (newText) =>
                                  onSaveFieldNameEdit(
                                    newText,
                                    i + (hasHeader ? 1 : 0),
                                    j
                                  )
                              : () => {
                                  return null;
                                }
                          }
                        />
                      </div>
                    </OverlayTrigger>
                  ) : (
                    column
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </>
    );
  };

  let headerRow;
  if (hasHeader) {
    headerRow = rows.splice(0, 1)[0];
  }
  return (
    <>
      {enableMerge && (
        <Row className="justify-content-start">
          <OverlayTrigger
            popperConfig={{
              modifiers: {
                preventOverflow: {
                  enabled: false,
                },
              } as any,
            }}
            placement="top"
            overlay={
              <Tooltip id="merge-rows">Merge selected rows together</Tooltip>
            }
          >
            <TrackedButton
              action="mergeRowsClick"
              id="merge-btn"
              className="m-2"
              variant="link"
              onClick={() => {
                if (onMerge) {
                  onMerge(selected);
                  const newSelected = {};
                  for (const i of Object.keys(selected)) {
                    newSelected[i] = false;
                  }
                  setSelected(newSelected);
                }
              }}
            >
              Merge rows
            </TrackedButton>
          </OverlayTrigger>
          <OverlayTrigger
            popperConfig={{
              modifiers: {
                preventOverflow: {
                  enabled: false,
                },
              } as any,
            }}
            placement="top"
            overlay={<Tooltip id="delete-rows">Delete selected rows</Tooltip>}
          >
            <TrackedButton
              action="mergeRowsClick"
              id="delete-btn"
              className="m-2"
              variant="link"
              onClick={() => {
                if (onDelete) {
                  onDelete(selected);
                  const newSelected = {};
                  for (const i of Object.keys(selected)) {
                    newSelected[i] = false;
                  }
                  setSelected(newSelected);
                }
              }}
            >
              Delete rows
            </TrackedButton>
          </OverlayTrigger>
        </Row>
      )}
      <table
        style={{
          whiteSpace: "nowrap",
          maxHeight,
          overflowY: "scroll",
        }}
        className={`csv-html-table table-responsive ${tableClassName}`}
      >
        {renderHeaderRowFn ? renderHeaderRowFn() : renderTableHeader(headerRow)}
        {renderTableBody(rows)}
      </table>
    </>
  );
};

CsvToHtmlTable.defaultProps = {
  editableCells: false,
  maxHeight: "500px",
};

export default CsvToHtmlTable;
